import {
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
} from "@flpstudio/design-system";
import { clsx } from "clsx";
import { Link, generatePath } from "react-router";

import { SoftwareDocumentRenderer } from "@/components/organisms/Document/renderer/SoftwareDocumentRenderer";
import { sendLinkPreviewClickEvent } from "@/components/organisms/Document/utils";
import { useAuth } from "@/hooks/use-auth";
import {
  Amplitude,
  DocumentInteractionsEvent,
  createDocumentInteractionsProperties,
} from "@/third-party/amplitude";
import type { SoftwareDocument } from "types";
import { paths } from "~/paths";
import { DocumentMeta } from "./DocumentMeta";

type Props = {
  document: SoftwareDocument;
};

export function SoftwareDocumentViewer({ document }: Props) {
  const { user } = useAuth();

  function handleSoftwareClick(software: string) {
    Amplitude.track(DocumentInteractionsEvent.name, {
      action: DocumentInteractionsEvent.properties.clickSoftwareTool.action,
      software,
      ...createDocumentInteractionsProperties(document, Boolean(user?.id)),
    });
  }

  return (
    <Paper className="-mx-6 lg:mx-0">
      <Text>
        We recommend the following tools. Please read through the expert
        guidance about each to make the best selection for your company.
      </Text>
      <Divider className="-mx-6 mt-6 mb-4" />
      <Group>
        {
          // Sort the software by order
          [document.software, ...(document.relatedSoftwares || [])]
            .sort((a, b) => (a?.order || 0) - (b?.order || 0))
            .map((software) => (
              <Button
                component={Link}
                variant="default"
                key={software?.id}
                to={generatePath(paths.documentation.viewer, {
                  urlSlug:
                    software?.documents?.[0]?.urlTitle ||
                    document.urlTitle || // if software doesn't have a document, it must be the current software document and therefore we should use the current document's url title
                    "",
                })}
                onClick={() => {
                  if (software?.name) {
                    handleSoftwareClick(software.name);
                  }
                }}
                className={clsx(
                  "border-[--mantine-color-gray-3] p-2",
                  software?.id === document.software?.id &&
                    "border-[--mantine-primary-color-filled] bg-[--mantine-primary-color-1]",
                )}
              >
                <img
                  src={software?.icon}
                  alt={software?.name}
                  loading="lazy"
                  className="mr-2 size-5 align-middle"
                />
                <span className="align-middle text-sm/normal">
                  {software?.name}
                </span>
              </Button>
            ))
        }
      </Group>
      <Divider className="-mx-6 mt-4 mb-6" />
      <Stack component="article">
        <DocumentMeta document={document} />
        <SoftwareDocumentRenderer
          document={document}
          onLinkPreviewClick={(url) =>
            sendLinkPreviewClickEvent({
              document,
              user,
              url,
            })
          }
        />
      </Stack>
    </Paper>
  );
}
